import { Icon } from '@iconify/react';
import { Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import Moment from 'moment';
import 'moment/locale/pt';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DataTable,
  StyledButton,
  StyledDateInput,
  StyledSelect,
} from '../../../../components';
import { ExportButton } from '../../../../components/ExportButton';
import { Loading } from '../../../../components/Loading';
import ExtractTypeEnum from '../../../../enums/ExtractTypeEnum';
import { api } from '../../../../services/api';
import { useFilters } from '../../../../services/contexts/FiltersContext';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import { useContaPagamento } from '../../../../services/hooks/useContaPagamento';
import { CnpjMask } from '../../../../utils/CnpjMask';
import { CpfMask } from '../../../../utils/CpfMask';
import { NewFormatValue } from '../../../../utils/NewFormatValue';
import { NinetyDaysAgo } from '../../../../utils/NinetyDaysAgo';
import { pluckProperty } from '../../../../utils/PluckProperty';
import { YearDaysFromNow } from '../../../../utils/YearDaysFromNow';
import { AdminTitle } from '../../components';
import { BarChart } from '../../components/BarChart';
import { ComprovanteModal } from '../../components/ComprovanteModal';
import CardBalance from './components/CardBalance';
import { ContaContainer, FiltersContainer, Row, Td } from './styles';
import { InputDescription } from '../../components/InputDescription';
import { groupDataByReference } from '../../../../utils/GroupDataByReference';
import { groupDataByMonth } from '../../../../utils/GroupDataByMonth';

const tableHead = [
  'Detalhes',
  'CPF ou CNPJ',
  'Data',
  'Valor da Transação',
  'Saldo disponível',
  'Operações',
  'Comprovante',
];

const tableHeadMonth = ['CPF ou CNPJ', 'Data', 'Saldo disponível'];
export interface BarChartProps {
  antecipacao: string;
  situacao: number;
  dtPrevPgto: string;
  vlBruto: number;
  vlDescontos: number;
  vlPago: number;
  vlAberto: number;
}

export const Extrato = () => {
  const { documentSelected } = useUserDocument();
  const docto = useMemo(() => documentSelected as string, [documentSelected]);
  const navigate = useNavigate();

  const now = DateTime.now()
    .setLocale('pt-br')
    .toLocaleString(DateTime.DATE_HUGE);

  const today = new Date();

  const {
    extractInitialDate,
    setExtractInitialDate,
    extractFinalDate,
    setExtractFinalDate,
  } = useFilters();
  const [agroup, setAgroup] = useState('D');

  const [isOpen, setIsOpen] = useState(false);
  const [id, setId] = useState('');

  const [openValue, setOpenValue] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [balances, setBalances] = useState<BarChartProps[]>([]);
  const [paginatedList, setPaginatedList] = useState<any>([]);
  const [groupedList, setGroupedList] = useState<any>([]);
  const [extractList, setExtractList] = useState<any>([]);

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  const handleAddLocaleDateString = (date: Date) => {
    if (date) {
      return date?.toLocaleDateString('pt-BR').split('/').reverse().join('-');
    }
  };

  const [params, setParams] = useState({
    docto: docto ?? '',
    dtInicial: handleAddLocaleDateString(extractInitialDate),
    dtFinal: handleAddLocaleDateString(extractFinalDate),
  });

  const handleFilter = useCallback(() => {
    const newParams = {
      docto: docto,
      dtInicial: handleAddLocaleDateString(extractInitialDate),
      dtFinal: handleAddLocaleDateString(extractFinalDate),
    };

    setParams(newParams);
  }, [docto, extractInitialDate, extractFinalDate]);

  const { data, isLoading, isFetching, error } = useContaPagamento(params);

  function setPagination(data: any, start: number, end: number) {
    if (!data) {
      return [];
    }

    return data.slice(start, end);
  }

  const handleAgroup = (value: string) => {
    if (value === 'M') {
      setEdgeMonthDays();
    }
    setAgroup(value);
  };

  const setEdgeMonthDays = () => {
    const startOfMonth = Moment(extractInitialDate).startOf('month').toDate();
    const endOfMonth = Moment(extractFinalDate).endOf('month').toDate();
    setExtractInitialDate(startOfMonth);
    setExtractFinalDate(endOfMonth);
  };

  const handleExtractFinalDate = (value: any) => {
    if (agroup === 'M') {
      const endOfMonth = Moment(value).endOf('month').toDate();
      setExtractFinalDate(endOfMonth);
    } else {
      setExtractFinalDate(value);
    }
  };

  const handleExtractInitialDate = (value: any) => {
    if (agroup === 'M') {
      const endOfMonth = Moment(value).startOf('month').toDate();
      setExtractInitialDate(endOfMonth);
    } else {
      setExtractInitialDate(value);
    }
  };

  useEffect(() => {
    if (docto) {
      setParams((previousState) => ({ ...previousState, docto: docto }));
    }

    if (itemsPerPage) {
      setPage(1);
    }
  }, [docto, itemsPerPage]);

  useEffect(() => {
    const dataGrouped = groupDataByReference(data?.extrato);
    const pagedData = setPagination(dataGrouped, startIndex, endIndex);

    setGroupedList(dataGrouped);

    setPaginatedList(pagedData);
  }, [data, itemsPerPage, page]);

  useEffect(() => {
    if (agroup === 'D') {
      setExtractList(paginatedList);
    }

    if (agroup === 'M') {
      const dataGroupedByMonth = groupDataByMonth(
        data?.extrato,
        extractInitialDate,
        extractFinalDate
      );

      const pagedDateByMonth = setPagination(
        dataGroupedByMonth,
        startIndex,
        endIndex
      );
      setGroupedList(dataGroupedByMonth);
      setExtractList(pagedDateByMonth);
    }
  }, [
    agroup,
    extractInitialDate,
    extractFinalDate,
    itemsPerPage,
    page,
    data,
    paginatedList,
  ]);

  useEffect(() => {
    handleFilter();
  }, [extractFinalDate, extractInitialDate]);

  useEffect(() => {
    if (docto?.length) {
      api
        .get<{
          openValue: number;
          currentBalance: number;
          balanceValues: any[];
        }>('/transactions/valoresAberto', { params: { docto } })
        .then((r) => {
          const { data } = r;

          setOpenValue(data.openValue);
          setCurrentBalance(data.currentBalance);
          setBalances(data.balanceValues);
        });
    }
  }, [docto]);

  return (
    <ContaContainer>
      <p className="date">{now}</p>
      <AdminTitle
        title="Movimentação Financeira > Extrato"
        icon={<Icon icon="tabler:pig-money" />}
      />
      {isOpen && (
        <ComprovanteModal
          isOpen={isOpen}
          onRequestClose={closeModal}
          id={id}
          docto={docto}
        />
      )}
      <div className="contentHeader">
        <p>Consulte seu extrato e saldo de operações</p>
      </div>
      <div className="contentCard">
        <div className="cards">
          <CardBalance
            icon="$"
            title="Saldo Disponível"
            color="#595044"
            footer="Saldo Total após Pagamentos Pendentes"
            isLoading={isLoading}
            isFetching={isFetching}
            value={currentBalance}
          />

          <CardBalance
            icon="$"
            title="Saldo a Receber"
            color="#F77701"
            footer="Saldo a ser Creditado nos próximos dias"
            isLoading={isLoading}
            isFetching={isFetching}
            value={openValue}
          />
        </div>

        <BarChart
          categories={pluckProperty(balances, 'dtPrevPgto')}
          dataValue={pluckProperty(balances, 'vlAberto')}
          isLoading={isLoading}
        />
      </div>
      {/* <div className="contentButton">
        <StyledButton
          icon={<Icon icon="clarity:plus-line" width={18} />}
          iconPosition="right"
          color="#595044"
          text="Simular Antecipação"
          onClick={() => navigate(`/admin/antecipacao`)}
        />
      </div> */}
      <FiltersContainer>
        <div>
          <InputDescription placeholder="Data inicial" />
          <StyledDateInput
            value={extractInitialDate}
            onChange={handleExtractInitialDate}
            placeholder="Data inicial"
            minDate={extractInitialDate ? NinetyDaysAgo(new Date()) : undefined}
            maxDate={today ? YearDaysFromNow(today) : today}
          />
        </div>

        <div>
          <InputDescription placeholder="Data final" />
          <StyledDateInput
            value={extractFinalDate}
            onChange={handleExtractFinalDate}
            placeholder="Data final"
            minDate={extractInitialDate ? NinetyDaysAgo(new Date()) : undefined}
            maxDate={today ? YearDaysFromNow(today) : today}
          />
        </div>

        <div>
          <InputDescription placeholder="Agrupar por" />
          <StyledSelect
            options={[
              { id: 'D', value: 'Dia' },
              { id: 'M', value: 'Mês' },
            ]}
            setValue={handleAgroup}
            value={agroup}
          />
        </div>
        <ExportButton
          params={{
            ...params,
            pagina: 1,
            limite: 10,
            groupedList,
            agroupBy: agroup,
          }}
          transactionType="extrato"
          disabled={isLoading || isFetching}
        />
      </FiltersContainer>

      {agroup === 'D' ? (
        <DataTable
          title="Extrato"
          heads={tableHead}
          hasPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          totalItems={extractList?.length}
        >
          <tbody>
            {isLoading || isFetching ? (
              <Loading />
            ) : error ? (
              <Row>
                <Td colSpan={11}>Falha ao obter dados</Td>
              </Row>
            ) : data?.extrato == null || data?.extrato?.length === 0 ? (
              <Row>
                <Td colSpan={11}>Não foram encontrados dados.</Td>
              </Row>
            ) : (
              extractList?.map((data: any, i: number) => (
                <Row key={i}>
                  <Td className="itemClicavel">
                    <Link
                      to={`/admin/extrato/${documentSelected}/${data?.id}`}
                      // target="_blank"
                    >
                      <Tooltip title="Visualizar detalhes" placement="top">
                        <div className="content-agroup-month">
                          <Icon
                            icon="material-symbols:search-rounded"
                            width="20"
                            height="20"
                          />
                        </div>
                      </Tooltip>
                    </Link>
                  </Td>
                  <Td>
                    {docto?.length <= 12
                      ? CpfMask(docto.padStart(11, '0'))
                      : CnpjMask(docto.padStart(14, '0'))}
                  </Td>
                  <Td>
                    {data?.dtMovimento
                      ? Moment(data?.dtMovimento)
                          .locale('pt')
                          .format('DD/MM/yyyy')
                      : ''}
                  </Td>

                  <Td hasValue={data?.vlMovimento}>
                    {NewFormatValue(data?.vlMovimento)}
                  </Td>

                  <Td>{NewFormatValue(data?.saldoAcumulado)}</Td>

                  <Td>
                    {ExtractTypeEnum[data.tipo as keyof typeof ExtractTypeEnum]}
                  </Td>
                  <Td>
                    {agroup === 'D' &&
                    data?.tipo === 'PGE' &&
                    data?.idComprovante ? (
                      <Tooltip
                        title="Visualizar ou Imprimir comprovante"
                        placement="top"
                      >
                        <button
                          onClick={() => {
                            setId(data?.idComprovante);
                            openModal();
                          }}
                        >
                          Visualizar
                        </button>
                      </Tooltip>
                    ) : (
                      ''
                    )}
                  </Td>
                </Row>
              ))
            )}
          </tbody>
        </DataTable>
      ) : (
        <DataTable
          title="Extrato"
          heads={tableHeadMonth}
          hasPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          totalItems={extractList?.length}
        >
          <tbody>
            {isLoading || isFetching ? (
              <Loading />
            ) : error ? (
              <Row>
                <Td colSpan={11}>Falha ao obter dados</Td>
              </Row>
            ) : data?.extrato == null || data?.extrato?.length === 0 ? (
              <Row>
                <Td colSpan={11}>Não foram encontrados dados.</Td>
              </Row>
            ) : (
              extractList?.map((data: any, i: number) => (
                <Row key={i}>
                  <Td>
                    {docto?.length <= 12
                      ? CpfMask(docto.padStart(11, '0'))
                      : CnpjMask(docto.padStart(14, '0'))}
                  </Td>
                  <Td>
                    {data?.dtMovimento
                      ? Moment(data?.dtMovimento)
                          .locale('pt')
                          .format('MMMM/yyyy')
                      : ''}
                  </Td>

                  <Td>{NewFormatValue(data?.saldoAcumulado)}</Td>
                </Row>
              ))
            )}
          </tbody>
        </DataTable>
      )}
    </ContaContainer>
  );
};
